import { openDB } from 'idb';

const DB_NAME = 'car-pdi-db';
const DB_VERSION = 1;

const STORES = {
  PDI_STATE: 'pdi-state',
  PHOTOS: 'photos',
  CAR_PROFILE: 'car-profile',
};

/**
 * Initialize the IndexedDB database
 */
const initDB = async () => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Store for PDI state (status, notes)
      if (!db.objectStoreNames.contains(STORES.PDI_STATE)) {
        db.createObjectStore(STORES.PDI_STATE);
      }
      
      // Store for photos
      if (!db.objectStoreNames.contains(STORES.PHOTOS)) {
        const photoStore = db.createObjectStore(STORES.PHOTOS, { 
          keyPath: 'id',
          autoIncrement: true 
        });
        photoStore.createIndex('itemId', 'itemId', { unique: false });
      }
      
      // Store for car profile
      if (!db.objectStoreNames.contains(STORES.CAR_PROFILE)) {
        db.createObjectStore(STORES.CAR_PROFILE);
      }
    },
  });
};

// PDI State Management
export const pdiStateDB = {
  async getItemStatuses() {
    const db = await initDB();
    return (await db.get(STORES.PDI_STATE, 'itemStatuses')) || {};
  },
  
  async saveItemStatuses(statuses) {
    const db = await initDB();
    await db.put(STORES.PDI_STATE, statuses, 'itemStatuses');
  },
  
  async getNotes() {
    const db = await initDB();
    return (await db.get(STORES.PDI_STATE, 'notes')) || {};
  },
  
  async saveNotes(notes) {
    const db = await initDB();
    await db.put(STORES.PDI_STATE, notes, 'notes');
  },
  
  async getResolvedItems() {
    const db = await initDB();
    return (await db.get(STORES.PDI_STATE, 'resolvedItems')) || {};
  },
  
  async saveResolvedItems(resolved) {
    const db = await initDB();
    await db.put(STORES.PDI_STATE, resolved, 'resolvedItems');
  },
  
  async getHasSeenGuidance() {
    const db = await initDB();
    return (await db.get(STORES.PDI_STATE, 'hasSeenGuidance')) || false;
  },
  
  async setHasSeenGuidance(seen) {
    const db = await initDB();
    await db.put(STORES.PDI_STATE, seen, 'hasSeenGuidance');
  },
  
  async clearAll() {
    const db = await initDB();
    const tx = db.transaction(STORES.PDI_STATE, 'readwrite');
    await tx.store.clear();
    await tx.done;
  },
};

// Photo Management
export const photosDB = {
  async getPhotosForItem(itemId) {
    const db = await initDB();
    const index = db.transaction(STORES.PHOTOS).store.index('itemId');
    return await index.getAll(itemId);
  },
  
  async getAllPhotos() {
    const db = await initDB();
    return await db.getAll(STORES.PHOTOS);
  },
  
  async addPhoto(itemId, photoData) {
    const db = await initDB();
    const id = await db.add(STORES.PHOTOS, {
      itemId,
      data: photoData,
      timestamp: Date.now(),
    });
    return id;
  },
  
  async deletePhoto(photoId) {
    const db = await initDB();
    await db.delete(STORES.PHOTOS, photoId);
  },

  async updatePhoto(photoId, newData) {
    const db = await initDB();
    const existing = await db.get(STORES.PHOTOS, photoId);
    if (!existing) return;
    await db.put(STORES.PHOTOS, { ...existing, data: newData, timestamp: Date.now() });
  },
  
  async deletePhotosForItem(itemId) {
    const db = await initDB();
    const photos = await this.getPhotosForItem(itemId);
    const tx = db.transaction(STORES.PHOTOS, 'readwrite');
    await Promise.all(photos.map(photo => tx.store.delete(photo.id)));
    await tx.done;
  },
  
  async clearAll() {
    const db = await initDB();
    const tx = db.transaction(STORES.PHOTOS, 'readwrite');
    await tx.store.clear();
    await tx.done;
  },
};

// Car Profile Management
export const carProfileDB = {
  async getProfile() {
    const db = await initDB();
    return await db.get(STORES.CAR_PROFILE, 'profile');
  },
  
  async saveProfile(profile) {
    const db = await initDB();
    await db.put(STORES.CAR_PROFILE, {
      ...profile,
      updatedAt: Date.now(),
    }, 'profile');
  },
  
  async clearProfile() {
    const db = await initDB();
    await db.delete(STORES.CAR_PROFILE, 'profile');
  },
};

// Full Reset
export const resetAllData = async () => {
  await pdiStateDB.clearAll();
  await photosDB.clearAll();
  await carProfileDB.clearProfile();
};

// Export data for PDF generation
export const exportData = async () => {
  const [statuses, notes, resolved, profile, photos] = await Promise.all([
    pdiStateDB.getItemStatuses(),
    pdiStateDB.getNotes(),
    pdiStateDB.getResolvedItems(),
    carProfileDB.getProfile(),
    photosDB.getAllPhotos(),
  ]);
  
  return {
    statuses,
    notes,
    resolved,
    profile,
    photoCount: photos.length,
    exportedAt: new Date().toISOString(),
  };
};
